import api from './api.service';

function unwrap<T>(res: any): T {
  const data = res?.data ?? res;
  return (data?.data ?? data) as T;
}

export interface ServiceReceiptLineDTO {
  id?: string;
  line_number?: number;
  po_item_id?: string | null;
  description?: string | null;
  /** @deprecated use description */
  item_description?: string;
  /** Technical specification snapshot from the PO line (read-only). */
  specifications?: string | null;
  work_quantity?: number;
  uom?: string;
  unit_rate?: number;
  line_value?: number;
  billed_value?: number;
  remarks?: string;
}

export interface ServiceReceiptDTO {
  id: string;
  company_id: string;
  sr_number: string;
  po_id: string;
  po_milestone_id: string;
  supplier_id: string;
  sr_date: string;
  service_provider_reference?: string | null;
  sr_status: string;
  /** Alias mapped from sr_status for UI convenience */
  status?: string;
  remarks?: string | null;
  progress_percentage?: number;
  claimed_value?: number;
  approved_value?: number;
  retention_amount?: number;
  rejected_value?: number;
  notes?: string;
  department_id?: string | null;
  created_at?: string;
  purchaseOrder?: { id: string; po_number?: string };
  milestone?: { id: string; milestone_number?: number; milestone_name?: string };
  supplier?: { id: string; supplier_name?: string };
  department?: { id: string; department_code?: string; department_name?: string };
  billed_value?: number;
  journal_voucher_id?: string | null;
  gl_posted?: boolean;
  provision_account_id?: string | null;
  /** Flat labels from BE enrich (preferred when present) */
  po_number?: string | null;
  supplier_name?: string | null;
  milestone_label?: string | null;
  department_label?: string | null;
  lines?: ServiceReceiptLineDTO[];
}

export interface CreateServiceReceiptDTO {
  po_id: string;
  po_milestone_id: string;
  sr_date: string;
  service_provider_reference?: string;
  progress_percentage?: number;
  claimed_value?: number;
  approved_value?: number;
  retention_amount?: number;
  remarks?: string;
  department_id?: string;
  lines?: ServiceReceiptLineDTO[];
}

export type UpdateServiceReceiptDTO = Partial<CreateServiceReceiptDTO>;

function mapServiceReceipt(row: ServiceReceiptDTO): ServiceReceiptDTO {
  return {
    ...row,
    status: row.sr_status ?? row.status,
    notes: row.remarks ?? row.notes,
    lines: (row.lines ?? []).map((l) => ({
      ...l,
      item_description: l.description ?? l.item_description,
      description: l.description ?? l.item_description,
    })),
  };
}

export const serviceReceiptService = {
  list(params?: { po_id?: string; status?: string }): Promise<ServiceReceiptDTO[]> {
    const q = new URLSearchParams();
    if (params?.po_id) q.set('po_id', params.po_id);
    if (params?.status) q.set('status', params.status);
    return api
      .get(`/procurement/service-receipts?${q.toString()}`)
      .then((res) => (unwrap<ServiceReceiptDTO[]>(res) ?? []).map(mapServiceReceipt));
  },

  getById(id: string): Promise<ServiceReceiptDTO> {
    return api
      .get(`/procurement/service-receipts/${id}`)
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  create(dto: CreateServiceReceiptDTO): Promise<ServiceReceiptDTO> {
    return api
      .post('/procurement/service-receipts', dto)
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  update(id: string, dto: UpdateServiceReceiptDTO): Promise<ServiceReceiptDTO> {
    return api
      .put(`/procurement/service-receipts/${id}`, dto)
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  submit(id: string): Promise<ServiceReceiptDTO> {
    return api
      .post(`/procurement/service-receipts/${id}/submit`, {})
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  approve(id: string): Promise<ServiceReceiptDTO> {
    return api
      .post(`/procurement/service-receipts/${id}/approve`, {})
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  post(id: string): Promise<ServiceReceiptDTO> {
    return api
      .post(`/procurement/service-receipts/${id}/post`, {})
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  cancel(id: string): Promise<ServiceReceiptDTO> {
    return api
      .post(`/procurement/service-receipts/${id}/cancel`, {})
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },

  unpost(id: string): Promise<ServiceReceiptDTO> {
    return api
      .post(`/procurement/service-receipts/${id}/unpost`, {})
      .then((res) => mapServiceReceipt(unwrap<ServiceReceiptDTO>(res)));
  },
};
